import { ItemType } from 'Shared/constants/types';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import has from 'lodash/has';

import { buildMeetingItemsPositions, sortFilterValues, sortByHighPriority } from 'Shared/utils/sorting';

export const showFilterValues = {
  SHOW_ALL: { label: 'All', value: 'ALL' },
  SHOW_DISCUSSION_TOPICS: { label: 'Discussion Topics', value: 'discussion_topic' },
  SHOW_ACTION_ITEMS: { label: 'Action Items', value: 'action_item' },
};

export const getMeetingItemByUuid = meetingItemUuid => state => {
  const { meetingItems } = state;
  return meetingItems.find(({ uuid }) => uuid === meetingItemUuid);
};

export const getMeetingItemById = meetingItemId => state => {
  const { meetingItems } = state;
  return meetingItems.find(({ id }) => id === meetingItemId);
};

export const getActionItems = state => state.meetingItems.filter(({ itemType }) => itemType === ItemType.actionItem);

export const getDiscussionTopics = state =>
  state.meetingItems.filter(({ itemType }) => itemType === ItemType.discussionTopic);

function getCompletedItemsList(meetingItems) {
  return meetingItems.filter(item => item.completed && !item.parentUuid);
}

export const getCompletedTopicsAndItemsContent = (showFilterValue, sortFilterValue) => state => {
  let listItems;
  let meetingItems;
  switch (showFilterValue.value) {
    case showFilterValues.SHOW_ACTION_ITEMS.value:
      meetingItems = getActionItems(state);
      listItems = getCompletedItemsList(meetingItems);
      break;
    case showFilterValues.SHOW_DISCUSSION_TOPICS.value:
      meetingItems = getDiscussionTopics(state);
      listItems = getCompletedItemsList(meetingItems);
      break;
    default:
      meetingItems = [...getActionItems(state), ...getDiscussionTopics(state)];
      listItems = getCompletedItemsList(meetingItems);
  }
  switch (sortFilterValue.value) {
    case sortFilterValues.DATE_DUE.value:
      listItems = sortBy(listItems, item => item.dueBy);
      break;
    case sortFilterValues.OWNER_A_Z.value:
      listItems = sortBy(listItems, item => item.assignee.personName);
      break;
    case sortFilterValues.OWNER_Z_A.value:
      listItems = sortBy(listItems, item => item.assignee.personName).reverse();
      break;
    default:
      listItems = sortBy(listItems, item => item.createdAt).reverse();
  }
  return listItems;
};

export const getChildrenItemsUuids = parentUuid => state => {
  const { meetingItems } = state;
  return meetingItems.filter(item => item.parentUuid === parentUuid).map(item => item.uuid);
};

export const getMeetingItemsByUuids = uuids => state => {
  const { meetingItems } = state;
  return meetingItems.filter(item => uuids.includes(item.uuid));
};

export const getMeetingItemAttachments = uuid => state => {
  const item = getMeetingItemByUuid(uuid)(state);
  return get(item, 'attachments', []);
};

export const getCommentByUuid = uuid => state => {
  const { meetingItems } = state;
  const meetingItem = meetingItems.find(item => item.comments.find(comment => comment.uuid === uuid));
  return meetingItem.comments.find(comment => comment.uuid === uuid);
};

export const getUniqAssignedUsers = () => state => {
  const itemsWithUniqUsers = uniqBy(state.meetingItems || [], 'assignmentPersonId');

  const uniqUsers = itemsWithUniqUsers.map(({ workspaceId, assignmentPersonId }) => {
    const workspace = state.workspaces.find(({ id }) => id === workspaceId);
    if (workspace) {
      const user = workspace.users.find(({ personId }) => personId === assignmentPersonId);
      return user;
    }
    return null;
  });

  return uniqUsers.filter(user => user);
};

export const getUniqUsersCompleter = meetingItems => {
  const meetingItemsWithUniqUsers = uniqBy(
    meetingItems.filter(({ completed }) => completed),
    'completedBy.id'
  );
  const uniqUsers = meetingItemsWithUniqUsers.map(({ completedBy }) =>
    completedBy
      ? {
          id: completedBy.id,
          personId: completedBy.personId,
          avatar: completedBy.avatar,
          name: completedBy.fullName || completedBy.email,
        }
      : null
  );
  return sortBy(uniqUsers, 'fullName');
};

export function getMeetingItemsWithAddedNesting(
  state,
  { draggedItemUuids, parentId, parentUuid, itemType, positionField }
) {
  const itemsWithAddedNesting = state.map(item => {
    if (draggedItemUuids.includes(item.uuid)) {
      return {
        ...item,
        parentId,
        parentUuid,
      };
    }
    return item;
  });

  const sortedItems = sortBy(itemsWithAddedNesting, [positionField]);

  return buildMeetingItemsPositions(
    itemType === ItemType.actionItem ? sortByHighPriority(sortedItems) : sortedItems,
    positionField
  );
}

export function getMeetingItemsAfterDrop(state, { draggedItemUuids, hoveredItemUuid, hoverOffset, positionField }) {
  const sortedItems = sortBy(state, [positionField]);
  const hoveredItem = sortedItems.find(({ uuid }) => uuid === hoveredItemUuid);
  const hoveredIndex = sortedItems.indexOf(hoveredItem);
  const draggedItems = sortedItems.filter(({ uuid }) => draggedItemUuids.includes(uuid));
  if (!draggedItems.length) return sortedItems;
  const countItemsHigherHoveredItem = draggedItems.filter(
    item => hoveredItem[positionField] >= item[positionField]
  ).length;

  const insertIndex = hoverOffset
    ? hoveredIndex - countItemsHigherHoveredItem + 1
    : hoveredIndex - countItemsHigherHoveredItem;

  const meetingItemsWithNewPositions = sortedItems.filter(({ uuid }) => !draggedItemUuids.includes(uuid));

  meetingItemsWithNewPositions.splice(
    insertIndex,
    0,
    ...draggedItems.map(draggedItem => ({
      ...draggedItem,
      parentId: draggedItemUuids.includes(draggedItem.parentId) ? draggedItem.parentId : hoveredItem.parentId,
      parentUuid: draggedItemUuids.includes(draggedItem.parentUuid) ? draggedItem.parentUuid : hoveredItem.parentUuid,
    }))
  );

  return buildMeetingItemsPositions(meetingItemsWithNewPositions, positionField);
}

export function getMeetingItemsWithRemovedNesting(state, { uuid: todoUuid, positionField }) {
  const todoForRemoveNesting = state.find(({ uuid }) => uuid === todoUuid);
  if (!todoForRemoveNesting) return state;
  const indexParentTodo = state.indexOf(state.find(({ uuid }) => uuid === todoForRemoveNesting.parentUuid));
  const newMeetingItems = state.filter(({ uuid }) => uuid !== todoForRemoveNesting.uuid);

  newMeetingItems.splice(indexParentTodo + 1, 0, { ...todoForRemoveNesting, parentId: null, parentUuid: null });

  return buildMeetingItemsPositions(sortBy(newMeetingItems, [positionField]), positionField);
}

export function getUpdatedMeetingItem(state, params) {
  const hasHighPriorityParam = has(params, 'isHighPriority');
  return params.removeFromWorkspace
    ? state.filter(({ uuid }) => uuid !== params.uuid)
    : state.map(todo => {
        if (todo.uuid === params.uuid) return { ...todo, ...params };
        if (hasHighPriorityParam && todo.parentUuid === params.uuid)
          return { ...todo, isHighPriority: params.isHighPriority };
        return todo;
      });
}
